<template>
  <v-app
    v-show="appearance"
    :class="['cy-app', {
      'cy-app--full-screen': isFullScreen,
      'cy-app--public-status': isPublicStatusPage,
    }]"
    :style="[{
      '--appearance-bg-color': _.get(backgroundColor, 'cssString'),
      '--appearance-text-color': _.get(textColor, 'cssString'),
      '--appearance-text-color-values': _.get(textColor, 'cssValues'),
    }]"
    :data-locale="$i18n.locale">
    <component
      v-if="isRouteReady"
      :variant="variant"
      :fit-on-screen="fitOnScreen"
      :is="layout">
      <router-view/>
    </component>
  </v-app>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useLayoutStore } from './store/pinia/layout'
import { useRootStore } from './store/pinia/root'
import { appMode } from './utils/config/mode'

export default {
  name: 'CyApp',
  components: {
    CyAppLayout: defineAsyncComponent(() => import('@/layouts/CyAppLayout.vue')),
    CyDefaultLayout: defineAsyncComponent(() => import('@/layouts/CyDefaultLayout.vue')),
    CyPublicLayout: defineAsyncComponent(() => import('@/layouts/CyPublicLayout.vue')),
  },
  computed: {
    ...mapState(useLayoutStore, {
      isFullScreen: (state) => state.isFullScreen,
    }),
    ...mapState(useRootStore, [
      'appearance',
    ]),
    isRouteReady () {
      return !!this.$route.name
    },
    layout () {
      const fileName = _.chain(`${_.get(this.$route, 'meta.layout.name', 'default')}-layout`)
        .camelCase()
        .upperFirst()
        .value()
      return `Cy${fileName}`
    },
    variant () {
      return _.get(this.$route, 'meta.layout.variant')
    },
    fitOnScreen () {
      return _.get(this.$route, 'meta.layout.fitOnScreen', false)
    },
    // eslint-disable-next-line vue/no-unused-properties
    _vuetify () {
      return appMode.isStagingOrDev ? this.$vuetify : null
    },
    isPublicStatusPage () {
      return this.$route.name === 'status' && !this.isAuthenticated
    },
    backgroundColor () {
      return this.appearance?.color
    },
    textColor () {
      return this.backgroundColor?.contrastingColor ?? null
    },
  },
  watch: {
    'appearance.favicon' (newVal) {
      if (newVal) this.setFavicon()
    },
  },
  async created () {
    window.addEventListener('beforeunload', () => sessionStorage.setItem('isRefreshing', true))
    await this.fetchAppearance()
    this.setFavicon()
    this.checkAppVersion()
  },
  methods: {
    ...mapActions(useRootStore, [
      'FETCH_APP_APPEARANCE',
    ]),
    async fetchAppearance () {
      const orgCanonical = this.$route.params.orgCanonical ?? this.orgCanonical
      await this.FETCH_APP_APPEARANCE(orgCanonical)
    },
    setFavicon () {
      const favicon = this.appearance?.favicon
      const currentEnv = _.findKey({
        dev: appMode.isDev,
        ephemeral: appMode.isEEnv,
        staging: appMode.isStaging && !appMode.isBeta,
        beta: appMode.isBeta,
      })

      if (!currentEnv && !favicon) return

      const DOMfavicons = document.querySelectorAll('[data-js="favicon"]')
      DOMfavicons.forEach((DOMfavicon) => {
        const isNewFaviconCycloidDefault = /console.cycloid/.test(favicon)
        const isOldFaviconCycloidDefault = /console.cycloid\/static/.test(DOMfavicon?.href)
        DOMfavicon.href = _.every([isNewFaviconCycloidDefault, isOldFaviconCycloidDefault])
          ? DOMfavicon.href.replace('/prod', `/${currentEnv}`)
          : favicon
      })
    },
    checkAppVersion () {
      const { VUE_APP_API_URL } = import.meta.env
      const { MODE } = import.meta.env
      const currentEnvironment = `[env: ${MODE}]---[api: ${VUE_APP_API_URL}]`
      if (_.isEmpty(localStorage.getItem(LSK.ENVIRONMENT))) localStorage.setItem(LSK.ENVIRONMENT, currentEnvironment)
      const hasEnvChanged = VUE_APP_API_URL && !_.isEqual(currentEnvironment, localStorage.getItem(LSK.ENVIRONMENT))

      if (_.isEmpty(localStorage.getItem(LSK.APP_VERSION))) localStorage.setItem(LSK.APP_VERSION, __APP_VERSION__)
      const hasNewVersion = !_.isEqual(__APP_VERSION__, localStorage.getItem(LSK.APP_VERSION))

      const clearStorageButKeep = (storage, keysToKeep) => {
        const itemsToKeep = {}
        keysToKeep.forEach((key) => {
          itemsToKeep[key] = storage.getItem(key)
        })
        storage.clear()
        keysToKeep.forEach((key) => {
          storage.setItem(key, itemsToKeep[key])
        })
      }

      // Keys to keep in localStorage and sessionStorage
      const keysToKeep = [LSK.APP_VERSION]

      // Clear specific keys from localStorage and sessionStorage
      if (hasEnvChanged && window.location.pathname !== '/oidc-popup-callback') {
        clearStorageButKeep(sessionStorage, keysToKeep)
        clearStorageButKeep(localStorage, keysToKeep)
      }

      // Clear local and session storage to avoid problems between switching between staging/local/beta etc.
      if (hasEnvChanged && appMode.isTest) {
        console.info(`[Cycloid] Local storage has been reset: switched from ${localStorage.getItem(LSK.ENVIRONMENT)} to ${currentEnvironment}`)
        localStorage.setItem(LSK.ENVIRONMENT, currentEnvironment)
      }

      // Update app version in local storage if it has changed
      if (hasNewVersion) {
        console.info(`[Cycloid] App version updated from ${localStorage.getItem(LSK.APP_VERSION)} to ${__APP_VERSION__}`)
        localStorage.setItem(LSK.APP_VERSION, __APP_VERSION__)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cy-app {
  position: relative;
  color: cy-get-color("primary");

  :deep(.v-application__wrap) {
    z-index: 0;
    flex-direction: row;
  }

  &--full-screen {
    :deep(
      :is(.cy-sidebar, .cy-toolbar)
    ) {
      display: none;
    }
  }

  &--public-status {
    background-color: cy-get-color("white");
  }
}

.v-menu__content.menuable__content__active {
  border-top: none;
  border-radius: 10px 0 10px 10px;
  box-shadow:
    0 5px 5px -3px cy-get-color("black", $alpha: 0.2),
    0 8px 10px 1px cy-get-color("black", $alpha: 0.15),
    0 10px 13px 4px cy-get-color("black", $alpha: 0.15);
}
</style>
